import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  fetchNearbyMachines,
  formatDistance,
  machineImageUrl,
  openMachineItems,
} from '../api/machines';
import { LOGO, TIMES_PRIME_LOGO_PURPLE } from '../utils/assets';
import './NearbyMachines.css';

function BackIcon() {
  return (
    <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
      <path d="M20 12H5M11 5l-7 7 7 7" fill="none" stroke="#111" strokeWidth="2.2"
        strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function MachineCard({ machine }) {
  const [imageFailed, setImageFailed] = useState(false);
  const image = machineImageUrl(machine.machineImagePath);

  return (
    <li className="nm-card">
      <div className="nm-card-top">
        {image && !imageFailed ? (
          <img
            className="nm-thumb"
            src={image}
            alt=""
            loading="lazy"
            onError={() => setImageFailed(true)}
          />
        ) : (
          <div className="nm-thumb" />
        )}
        <div className="nm-info">
          {/* Floated first so the name and address wrap around it and use the full width below. */}
          <span className="nm-distance">{formatDistance(machine.distance)}</span>
          <h2 className="nm-name">{machine.name}</h2>
          <p className="nm-location">{machine.address}</p>
        </div>
      </div>
      <button
        className="nm-app-btn"
        type="button"
        onClick={() => openMachineItems(machine.id)}
      >
        Find on Daalchini App
      </button>
    </li>
  );
}

function SkeletonCard() {
  return (
    <li className="nm-card nm-skeleton" aria-hidden="true">
      <div className="nm-card-top">
        <div className="nm-thumb" />
        <div className="nm-info">
          <div className="nm-bar nm-bar-lg" />
          <div className="nm-bar nm-bar-sm" />
        </div>
      </div>
      <div className="nm-bar nm-bar-btn" />
    </li>
  );
}

function getPosition() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Location is not supported on this device.'));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      (err) =>
        reject(
          new Error(
            err.code === err.PERMISSION_DENIED
              ? 'Allow location access to see vending machines near you.'
              : "We couldn't get your location. Please try again."
          )
        ),
      // Rough location is enough to find nearby machines, and much faster than a GPS fix.
      { enableHighAccuracy: false, timeout: 15000, maximumAge: 60000 }
    );
  });
}

// status: 'locating' | 'loading' | 'ready' | 'error'
const INITIAL_STATE = { status: 'locating', machines: [], error: null };

function useNearbyMachines() {
  const [state, setState] = useState(INITIAL_STATE);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    setState(INITIAL_STATE);

    getPosition()
      .then((coords) => {
        if (controller.signal.aborted) return null;
        setState((s) => ({ ...s, status: 'loading' }));
        return fetchNearbyMachines({ ...coords, signal: controller.signal });
      })
      .then((machines) => {
        if (machines && !controller.signal.aborted) {
          setState({ status: 'ready', machines, error: null });
        }
      })
      .catch((err) => {
        if (controller.signal.aborted) return;
        setState({ status: 'error', machines: [], error: err.message || 'Something went wrong.' });
      });

    return () => controller.abort();
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);
  return { ...state, retry };
}

function NearbyMachines() {
  const navigate = useNavigate();
  const { status, machines, error, retry } = useNearbyMachines();
  const busy = status === 'locating' || status === 'loading';

  return (
    <div className="nm-page">
      <header className="nm-header">
        <div className="nm-header-inner">
          <button
            className="nm-back"
            type="button"
            aria-label="Go back"
            onClick={() => navigate('/')}
          >
            <BackIcon />
          </button>
          <div className="nm-brands">
            <img className="nm-logo" src={LOGO} alt="Daalchini" />
            <span className="nm-x">X</span>
            <img className="nm-times-prime" src={TIMES_PRIME_LOGO_PURPLE} alt="Times Prime" />
          </div>
        </div>
      </header>

      <main className="nm-main">
        <h1 className="nm-title">Nearby Machines</h1>
        <p className="nm-count">
          {status === 'locating' && 'Finding your location…'}
          {status === 'loading' && 'Searching nearby…'}
          {status === 'ready' && `${machines.length} Results`}
        </p>

        {status === 'error' && (
          <div className="nm-message">
            <p>{error}</p>
            <button className="nm-app-btn nm-retry" type="button" onClick={retry}>
              Try again
            </button>
          </div>
        )}

        {status === 'ready' && machines.length === 0 && (
          <div className="nm-message">
            <p>No vending machines found near you yet.</p>
          </div>
        )}

        <ul className="nm-list" aria-busy={busy}>
          {busy &&
            Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} />)}
          {status === 'ready' &&
            machines.map((machine) => <MachineCard key={machine.id} machine={machine} />)}
        </ul>
      </main>
    </div>
  );
}

export default NearbyMachines;
